import React from "react";
import { Button } from "reactstrap";
import { enqueueSnackbar } from "notistack";
import Swal from "sweetalert2";

import { BRAND_BLUE } from "../constants/assetValuationConstants";

const MoveToUnreviewedButton = ({
    selectedAsset,
    closeEditModal,
    unreviewAssetById,
    disabled,
    title,
}) => (
    <Button
        color="secondary"
        onClick={async () => {
            const assetId = selectedAsset?.id;
            if (!assetId) return;
            const confirmed = await Swal.fire({
                title: "Move to Unreviewed?",
                text: "This will remove review-incomplete status and move the asset to Un Reviewed list.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Move",
                cancelButtonText: "Cancel",
                confirmButtonColor: BRAND_BLUE,
            });
            if (!confirmed.isConfirmed) return;
            try {
                await unreviewAssetById(assetId);
                closeEditModal();
            } catch (error) {
                console.error("Failed to move asset to unreviewed:", error);
                enqueueSnackbar("Failed to move asset to unreviewed.", { variant: "error" });
            }
        }}
        disabled={disabled}
        title={title}
    >
        Move to Unreviewed
    </Button>
);

const RemoveRemarkIssueButton = ({
    selectedAsset,
    closeEditModal,
    clearReviewRemarkIssueByAssetId,
    disabled,
    title,
}) => (
    <Button
        color="info"
        onClick={async () => {
            const assetId = selectedAsset?.id;
            if (!assetId) return;
            const confirmed = await Swal.fire({
                title: "Remove Remark Issue?",
                text: "This will remove issue keywords and keep the asset under Review Incomplete.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Remove Issue",
                cancelButtonText: "Cancel",
                confirmButtonColor: BRAND_BLUE,
            });
            if (!confirmed.isConfirmed) return;
            try {
                await clearReviewRemarkIssueByAssetId(assetId);
                closeEditModal();
            } catch (error) {
                console.error("Failed to remove remark issue:", error);
                enqueueSnackbar("Failed to remove remark issue.", { variant: "error" });
            }
        }}
        disabled={disabled}
        title={title}
    >
        Remove Remark Issue
    </Button>
);

const getCompletionTitle = ({
    canMarkBuildingReviewed,
    canCompleteSiteWorkStep,
    siteWorkMessage,
}) => {
    if (!canMarkBuildingReviewed) return "Save or remove all building slots before finishing.";
    if (!canCompleteSiteWorkStep) return siteWorkMessage;
    return "";
};

const BuildingReviewFooter = ({
    editBuildingStepIndex,
    selectedBuildingDetailSlot,
    setSelectedBuildingDetailSlot,
    setEditBuildingStepIndex,
    savingEditAsset,
    savingSecurityOverview,
    savingSiteWork,
    handleMarkEditAssetReviewNotCompleted,
    handleSaveBuildingAssetAndNext,
    handleSaveSecurityOverviewAndNext,
    loadingSecurityOverview,
    isIncompleteTab,
    isRemarkIssuesTab,
    selectedEditAssetDraft,
    closeEditModal,
    unreviewAssetById,
    clearReviewRemarkIssueByAssetId,
    loadingEditBuildings,
    loadingEditSiteWorks,
    canMarkBuildingReviewed,
    canCompleteSiteWorkStep,
    selectedEditAssetHasSiteWork,
    handleGoToBuildingSiteWorks,
    handleMarkBuildingAssetReviewed,
    isViewOnlyMode,
}) => {
    const buildingsCompletionTitle = getCompletionTitle({
        canMarkBuildingReviewed,
        canCompleteSiteWorkStep,
        siteWorkMessage: "Go to Site Works and add at least one site work before finishing.",
    });
    const siteWorksCompletionTitle = getCompletionTitle({
        canMarkBuildingReviewed,
        canCompleteSiteWorkStep,
        siteWorkMessage: "Add at least one site work before finishing.",
    });

    return (
        <>
            {editBuildingStepIndex > 0 && (
                <Button
                    color="light"
                    onClick={() => {
                        if (editBuildingStepIndex === 2 && selectedBuildingDetailSlot) {
                            setSelectedBuildingDetailSlot(null);
                            return;
                        }
                        setEditBuildingStepIndex((prev) => Math.max(prev - 1, 0));
                    }}
                    disabled={savingEditAsset || savingSecurityOverview || savingSiteWork}
                >
                    Back
                </Button>
            )}
            {!isViewOnlyMode && (
                <Button
                    color="warning"
                    onClick={handleMarkEditAssetReviewNotCompleted}
                    disabled={savingEditAsset}
                >
                    Mark to be Reviewed Later
                </Button>
            )}
            {!isViewOnlyMode && editBuildingStepIndex === 0 && (
                <Button color="primary" onClick={handleSaveBuildingAssetAndNext} disabled={savingEditAsset}>
                    {savingEditAsset ? "Saving..." : "Save & Next"}
                </Button>
            )}
            {!isViewOnlyMode && editBuildingStepIndex === 1 && (
                <Button
                    color="primary"
                    onClick={handleSaveSecurityOverviewAndNext}
                    disabled={savingSecurityOverview || loadingSecurityOverview}
                >
                    {savingSecurityOverview ? "Saving..." : "Save & Next"}
                </Button>
            )}
            {!isViewOnlyMode && editBuildingStepIndex === 2 && (
                <>
                    {isIncompleteTab && (
                        <MoveToUnreviewedButton
                            selectedAsset={selectedEditAssetDraft}
                            closeEditModal={closeEditModal}
                            unreviewAssetById={unreviewAssetById}
                            disabled={loadingEditBuildings || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                            title={buildingsCompletionTitle}
                        />
                    )}
                    {isRemarkIssuesTab && (
                        <RemoveRemarkIssueButton
                            selectedAsset={selectedEditAssetDraft}
                            closeEditModal={closeEditModal}
                            clearReviewRemarkIssueByAssetId={clearReviewRemarkIssueByAssetId}
                            disabled={loadingEditBuildings || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                            title={buildingsCompletionTitle}
                        />
                    )}
                    {selectedBuildingDetailSlot ? (
                        <Button color="primary" outline onClick={() => setSelectedBuildingDetailSlot(null)}>
                            Finish Building
                        </Button>
                    ) : null}
                    {selectedEditAssetHasSiteWork && !selectedBuildingDetailSlot && (
                        <Button color="primary" onClick={handleGoToBuildingSiteWorks} disabled={loadingEditBuildings}>
                            Go to Site Works
                        </Button>
                    )}
                    <Button
                        color="success"
                        onClick={handleMarkBuildingAssetReviewed}
                        disabled={loadingEditBuildings || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                        title={buildingsCompletionTitle}
                    >
                        Finish
                    </Button>
                </>
            )}
            {!isViewOnlyMode && editBuildingStepIndex === 3 && (
                <>
                    {isIncompleteTab && (
                        <MoveToUnreviewedButton
                            selectedAsset={selectedEditAssetDraft}
                            closeEditModal={closeEditModal}
                            unreviewAssetById={unreviewAssetById}
                            disabled={loadingEditSiteWorks || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                            title={siteWorksCompletionTitle}
                        />
                    )}
                    {isRemarkIssuesTab && (
                        <RemoveRemarkIssueButton
                            selectedAsset={selectedEditAssetDraft}
                            closeEditModal={closeEditModal}
                            clearReviewRemarkIssueByAssetId={clearReviewRemarkIssueByAssetId}
                            disabled={loadingEditSiteWorks || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                            title={siteWorksCompletionTitle}
                        />
                    )}
                    <Button
                        color="success"
                        onClick={handleMarkBuildingAssetReviewed}
                        disabled={loadingEditSiteWorks || !canMarkBuildingReviewed || !canCompleteSiteWorkStep}
                        title={siteWorksCompletionTitle}
                    >
                        Finish
                    </Button>
                </>
            )}
        </>
    );
};

export default BuildingReviewFooter;
